import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { sessionQuery } from '@/services/session';
import { NoWorkspace } from '@/components/app/no-workspace';
import { AppShell } from '@/components/shell/app-shell';
import { identify } from '@/lib/observability';
import { syncMockMode } from '@/features/mock-mode/sync';
import { AddDraftMount } from '@/features/add-draft/add-draft-mount';
import { BriefChangesMount } from '@/features/brief-memory/brief-changes-mount';
import { RobotLooksMount } from '@/features/robots/robot-looks-mount';

/**
 * Every signed-in page lives under this layout. `beforeLoad` is the guard
 * web-v2 split across AuthGuard + TeamGuard:
 *   1. no session → /auth/signin?returnTo=<where they were going>; so does
 *      a session the API turns away (401 on the team read), once it's ended
 *   2. no team → the "no workspace yet" screen (web-v2 sends people to its
 *      onboarding, which web-v3 does not have yet)
 * In between, a Dreamwell person with mock mode on (`lib/mock-mode.ts`) has
 * the API client switched to the invented brand before anything reads, and
 * the Draper chat is pointed at her chair's surface. The reads go through the
 * Query cache, so navigating between pages costs nothing after the first load.
 */
export const Route = createFileRoute('/_authed')({
  beforeLoad: async ({ context: { queryClient }, location }) => {
    const user = await queryClient.ensureQueryData(sessionQuery);
    if (!user) {
      throw redirect({ to: '/auth/signin', search: { returnTo: location.href } });
    }
    const mock = await syncMockMode(user.email, queryClient);
    // Imported here, not at the top: the team service (and zod with it) then
    // loads with the signed-in app, not in the entry the sign-in page pays for.
    const { currentTeamQuery, isApiKeySession } = await import('@/services/team');
    const team = await queryClient.ensureQueryData(currentTeamQuery).catch(async (error: unknown) => {
      const { signInAgain } = await import('@/features/session/sign-in-again');
      return signInAgain(queryClient, error, location.href);
    });
    if (team) {
      // Which chair she sits in (the New operator's or a client's): the surface the chat talks to, also before it reads.
      const { syncViewerChair } = await import('@/features/viewer-chair/chair');
      await syncViewerChair(user.id, team.id, queryClient, { client: team.role === 'managed' });
      // The invented brand is never reported as a workspace she uses.
      if (!mock) identify(user, team);
      if (isApiKeySession(team)) {
        // An API key's role is the Owner's, cached before any loader reads it: the server always refuses a key.
        const { seedApiKeySeat } = await import('@/features/operator/api-key-seat');
        seedApiKeySeat(queryClient, team);
      } else {
        // Her role narrows the dock, Home and Review; start reading it now, without holding the page.
        void import('@/services/team-members').then(({ myRoleQuery }) => queryClient.prefetchQuery(myRoleQuery(team.id)));
      }
    }
    return { user, team, mock };
  },
  component: AuthedLayout,
});

function AuthedLayout() {
  const { team } = Route.useRouteContext();
  if (!team) return <NoWorkspace />;
  return (
    <AppShell>
      <Outlet />
      {/* "Add a draft" (openAddDraft from Review, Artifacts, Operations): its card loads on first use. */}
      <AddDraftMount />
      {/* "Brief changes" (openBriefChanges from Review Content, Draper's corner, Home, Draper chat, Operations): loads on first use. */}
      <BriefChangesMount />
      <RobotLooksMount />
    </AppShell>
  );
}
